/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import { FC, useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

import fitToScreenDisabled from '../../assets/icons/fit-to-screen-disabled.svg';
import fitToScreenEnabled from '../../assets/icons/fit-to-screen-enabled.svg';
import zoomInSvg from '../../assets/icons/zoom-in.svg';
import zoomOutSvg from '../../assets/icons/zoom-out.svg';
import { useLanguage } from '../../providers/LanguageProvider';
import { DataModel } from '../../types/gantt-types';
import { findMaxDuration, formatData, getDurationUnit } from '../../utils/gantt-utils';
import { formatDuration } from '../../lib/utils';
import { chevronDownIcon, chevronRightIcon, errorIcon } from './svg';

type ChartInternalOptions = {
  svg?: d3.Selection<SVGSVGElement, unknown, HTMLElement, any>;
  timelineSvg?: d3.Selection<SVGSVGElement, unknown, HTMLElement, any>;
  dateFormat?: (date: Date) => string;
  linearScale?: d3.ScaleLinear<number, number, never>;
  data: DataModel[];
  width: number;
  fixedPanel: d3.Selection<SVGGElement, unknown, HTMLElement, any>;
  scrollablePanel: d3.Selection<SVGGElement, unknown, HTMLElement, any>;
};

interface InputProps {
  data: DataModel[];
  onClick?: (id: string) => void;
  selectedSpanId?: string;
  onTraceExpand?: (traceId: string) => void;
  allTracesForFactId?: DataModel[];
  loadingTraceId?: string | null;
  setLoadingTraceId?: (id: string | null) => void;
  errorTraceId?: string | null;
  errorTraceMessage?: string | null;
}

type TBarPositionMap = {
  [key: string]: {
    x: number;
    y: number;
    height: number;
    width: number;
    startms: number;
    curveType: 'Single-Beizer' | 'Double-Beizer';
    leftOffsetForConnectingLine: number;
    parentId?: string;
    spanType?: string;
  };
};

const ROW_HEIGHT = 50;
const HEIGHT_OFFSET = 100;
const ZOOM_CONTROL_AREA_HEIGHT = 100;
const ZOOM_FACTOR = 50;
const LEFT_PANEL_WIDTH = 50;
const EMPTY_ROWS_COUNT = 4;
const CONNECTING_LINE_LEFT_OFFSET = 10;
const TEXT_IN_BARS_LEFT_OFFSET = 7;
const MAX_VISIBLE_ROWS = 12;
const hoverFill = 'rgba(255,255,255,0.12)';
const normalFill = 'rgba(255,255,255,0)';

export const GanttChart: FC<InputProps> = ({
  data,
  onClick,
  selectedSpanId,
  onTraceExpand,
  allTracesForFactId,
  loadingTraceId,
  setLoadingTraceId,
  errorTraceId,
  errorTraceMessage,
}) => {
  const bufferSpaceWidth = 120;

  const [extendedWidth, setExtendedWidth] = useState(0);
  const [isChartZoomed, setIsChartZoomed] = useState(false);
  const [maxDuration, setMaxDuration] = useState(0);
  const [options, setOptions] = useState<ChartInternalOptions | undefined>();
  const [formattedData, setFormattedData] = useState<DataModel[]>([]);
  const [isColapsed, setIsCollapsed] = useState<boolean>(false);
  const [rowCollapsed, setRowCollapsed] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [containerWidth, setContainerWidth] = useState(0);
  const { messages } = useLanguage();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const fixedPanelRef = useRef<SVGGElement | null>(null);
  const scrollablePanelRef = useRef<SVGGElement | null>(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const previousDataRef = useRef<string>('');
  const [localLoadingTraceId, setLocalLoadingTraceId] = useState<string | null>(
    null
  );
  let visibleIndex = -1;

  useEffect(() => {
    const container = document.getElementById('cw-gantt-chart-container');
    if (!container) return;

    const observer = new ResizeObserver(() => {
      const svg = document.getElementById('gantt-chart-svg');
      const contentHeight = svg?.clientHeight || 0;
      let widthForScrollbar = 5;
      if (contentHeight >= MAX_VISIBLE_ROWS * ROW_HEIGHT) {
        widthForScrollbar = 10;
      }
      const width = container.clientWidth - widthForScrollbar;
      setContainerWidth(width);
      setExtendedWidth(width);
    });

    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  // Auto-load first trace on mount/pagination for both grouped and single trace
  useEffect(() => {
    if (data.length === 0) return;

    const factNode = data.find((node) => node.spanType === 'fact');
    let firstTrace: DataModel | undefined;

    if (factNode && factNode.children && factNode.children.length > 0) {
      firstTrace = factNode.children[0];
    } else if (!factNode && data[0].spanType === 'workflow') {
      firstTrace = data[0];
    }

    if (
      firstTrace &&
      (!firstTrace.children || firstTrace.children.length === 0) &&
      firstTrace.spanType === 'workflow'
    ) {
      if (onTraceExpand && allTracesForFactId && !loadingTraceId) {
        setLocalLoadingTraceId(firstTrace.id);
        if (setLoadingTraceId) setLoadingTraceId(firstTrace.id);
        onTraceExpand(firstTrace.id);
      }
    }
  }, [data]);

  // Auto-expand trace after spans are fetched
  useEffect(() => {
    if (localLoadingTraceId && data.length > 0) {
      const loadedNode = findNodeById(data, localLoadingTraceId);

      // Check if spans have been loaded
      if (loadedNode && loadedNode.entityCount !== undefined) { // check if node data has been fetched
        if (loadedNode.children && loadedNode.children.length > 0) { // check if node has children before triggering expansion
          setRowCollapsed((prev) => {
            const newState = { ...prev };
            expandNLevels(loadedNode, newState, 3);
            return newState;
          });

          if (onClick) {
            onClick(localLoadingTraceId);
          }
        }
        setLocalLoadingTraceId(null);
        if (setLoadingTraceId) setLoadingTraceId(null);
      }
    }
  }, [data, localLoadingTraceId]);

  // Collapse the errored node to reset chevron
  useEffect(() => {
    if (errorTraceId) {
      setRowCollapsed((prev) => {
        const newState = { ...prev };
        newState[errorTraceId] = true;
        return newState;
      });
      setLocalLoadingTraceId(null);
    }
  }, [errorTraceId]);

  useEffect(() => {
    const container = document.getElementById('gantt-chart-container');
    if (!container?.clientWidth) return;

    const maxHeight = MAX_VISIBLE_ROWS * ROW_HEIGHT;
    container.style.maxHeight = `${maxHeight}px`;
    container.style.overflowY = 'auto';
    const formattedData = formatData([...data]);
    setFormattedData(formattedData);

    const maxDur = findMaxDuration(formattedData);
    setMaxDuration(maxDur);

    const currentFirstId = data?.[0]?.id || '';
    if (
      (isFirstLoad || previousDataRef.current !== currentFirstId) &&
      data?.length
    ) {
      previousDataRef.current = currentFirstId;
      setIsFirstLoad(false);

      setRowCollapsed(() => {
        const collapsedState: Record<string, boolean> = {};

        const collapseAll = (node: DataModel) => {
          collapsedState[node.id] = true;
          if (node.children) node.children.forEach(collapseAll);
        };

        const hasFact = formattedData.some((d) => d.spanType === 'fact');

        if (hasFact) {
          formattedData.forEach((item) => {
            if (item.spanType === 'fact') {
              collapsedState[item.id] = false;
              if (item.children) {
                item.children.forEach((trace, idx) => {
                  if (idx === 0) {
                    expandNLevels(trace, collapsedState, 3);
                  } else {
                    collapseAll(trace);
                  }
                });
              }
            } else {
              collapseAll(item);
            }
          });
        } else {
          formattedData.forEach((trace, idx) => {
            if (idx === 0) {
              expandNLevels(trace, collapsedState, 3);
            } else {
              collapseAll(trace);
            }
          });
        }

        // If selectedSpanId exists, expand all its parents
        if (selectedSpanId) {
          expandParentsOfSpan(selectedSpanId, formattedData, collapsedState);
        }
        return collapsedState;
      });
    }
  }, [data, containerWidth, isFirstLoad, selectedSpanId]);

  useEffect(() => {
    const height =
      getVisibleDataLength(formattedData) * ROW_HEIGHT +
      EMPTY_ROWS_COUNT * ROW_HEIGHT;

    d3.selectAll('#timeline-svg').remove();
    const timelineSvg = d3
      .select('#timeline-axis-container')
      .append('svg')
      .attr('id', 'timeline-svg')
      .attr('width', containerWidth)
      .attr('height', 50);

    d3.selectAll('#gantt-chart-svg').remove();
    const svg = d3
      .select('#gantt-chart-container')
      .append('svg')
      .attr('id', 'gantt-chart-svg')
      .attr('width', containerWidth)
      .attr('height', height)
      .style('overflow', 'scroll');

    svgRef.current = svg.node() as SVGSVGElement;

    const fixedPanel = svg
      .append('g')
      .attr('id', 'fixed-panel')
      .attr('clip-path', 'url(#fixed-clip)');

    fixedPanelRef.current = fixedPanel.node() as SVGGElement;

    svg
      .append('clipPath')
      .attr('id', 'fixed-clip')
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', LEFT_PANEL_WIDTH + bufferSpaceWidth)
      .attr('height', height);

    const scrollablePanelParent = svg
      .append('g')
      .attr('id', 'scrollable-panel-parent')
      .attr('clip-path', 'url(#scroll-clip)');

    const scrollablePanel = scrollablePanelParent
      .append('g')
      .attr('id', 'scrollable-panel')
      .attr('x', LEFT_PANEL_WIDTH + bufferSpaceWidth)
      .attr('y', 0)
      .attr('width', containerWidth - LEFT_PANEL_WIDTH - bufferSpaceWidth)
      .attr('cursor', 'grab')
      .attr('height', height);

    scrollablePanelRef.current = scrollablePanel.node() as SVGGElement;

    svg
      .append('clipPath')
      .attr('id', 'scroll-clip')
      .append('rect')
      .attr('x', LEFT_PANEL_WIDTH + bufferSpaceWidth)
      .attr('y', 0)
      .attr(
        'width',
        Math.max(0, containerWidth - LEFT_PANEL_WIDTH - bufferSpaceWidth)
      )
      .attr('height', height);

    const dateFormat = d3.utcFormat('%Y-%m-%d');
    const contentWidth = containerWidth - LEFT_PANEL_WIDTH - bufferSpaceWidth;

    const linearScale = d3
      .scaleLinear()
      .domain([0, 100])
      .range([0, contentWidth]);

    const options: ChartInternalOptions = {
      data: formattedData,
      width: containerWidth,
      svg,
      timelineSvg,
      fixedPanel,
      scrollablePanel,
      dateFormat,
      linearScale,
    };
    setOptions(options);

    setupPanningEvents(svg.node() as SVGSVGElement, scrollablePanelRef.current);
  }, [formattedData, extendedWidth, rowCollapsed]);

  const setupPanningEvents = (
    svg: SVGSVGElement,
    scrollablePanel: SVGGElement
  ) => {
    if (
      !options ||
      !options.width ||
      !svg ||
      !scrollablePanel ||
      extendedWidth === options.width
    )
      return;
    let startX = 0;
    let currentTranslateX = 0;
    let isDragging = false;

    const visibleWidth = containerWidth - LEFT_PANEL_WIDTH - bufferSpaceWidth;
    const maxPanDistance = extendedWidth - visibleWidth;

    const handleMouseDown = (event: MouseEvent) => {
      isDragging = true;
      startX = event.clientX;

      const transform = d3.select(scrollablePanel).attr('transform');
      if (transform) {
        const match = /translate\(([^,]+),/.exec(transform);
        if (match) {
          currentTranslateX =
            parseFloat(match[1]) - LEFT_PANEL_WIDTH - bufferSpaceWidth;
        }
      }

      event.preventDefault();
    };

    const handleMouseMove = (event: MouseEvent) => {
      if (!isDragging) return;

      const dx = event.clientX - startX;
      let newTranslateX = currentTranslateX + dx;

      newTranslateX = Math.min(
        -LEFT_PANEL_WIDTH - bufferSpaceWidth,
        Math.max(-maxPanDistance, newTranslateX)
      );

      d3.select(scrollablePanel).attr(
        'transform',
        `translate(${LEFT_PANEL_WIDTH + bufferSpaceWidth + newTranslateX}, 0)`
      );

      event.preventDefault();
    };

    const handleMouseUp = () => {
      isDragging = false;
    };

    svg.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      svg.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  };

  function clearAllSelections() {
    d3.selectAll("[id^='chartBar-scrollable-']").attr('fill', normalFill);
    d3.selectAll("[id^='chartBar-fixed-']").attr('fill', normalFill);
  }

  useEffect(() => {
    clearAllSelections();
    if (!selectedSpanId) return;

    const selectedFill = hoverFill;

    const scrollableBar = document.getElementById(`chartBar-scrollable-${selectedSpanId}`);
    if (scrollableBar) {
      d3.select(scrollableBar).attr('fill', selectedFill);
    }
    const fixedBar = document.getElementById(`chartBar-fixed-${selectedSpanId}`);
    if (fixedBar) {
      d3.select(fixedBar).attr('fill', selectedFill);
    }
  }, [selectedSpanId]);

  useEffect(() => {
    if (options) {
      startDrawing();
    }
  }, [options, selectedSpanId, bufferSpaceWidth, rowCollapsed, localLoadingTraceId, loadingTraceId, errorTraceId, errorTraceMessage]);

const onChevronClick = (id: string) => {
  // Find the node being toggled
  const node = findNodeById(formattedData, id);
  
  // Check if it's a placeholder workflow node
  const isPlaceholder = node && 
                        (!node.children || node.children.length === 0) && 
                        node.spanType === 'workflow';
  
  if (isPlaceholder) {
    // Fetch spans for this trace
    if (onTraceExpand && allTracesForFactId) {
      const trace = allTracesForFactId.find((t: any) => t.trace_id === id);
      if (trace) {
        setLocalLoadingTraceId(id);
        if (setLoadingTraceId) setLoadingTraceId(id);
        onTraceExpand(id);
        return;
      }
    }
  }
  
  // Normal toggle logic for non-placeholder nodes
  setRowCollapsed((prev) => {
    const newState = { ...prev };
    const isExpanding = newState[id];
    newState[id] = !newState[id];

    if (isExpanding && node) {
      expandNLevels(node, newState, 3);
    }

    return newState;
  });

  if (!rowCollapsed[id]) {
    setIsCollapsed(false);
  }
};

  const startDrawing = () => {
    if (options && options.svg) {
      drawAxis();
      drawHeaderColumn();
      drawRects();
      drawVerticalLines();
      drawEndLine();
      drawZoomControls();
    }
  };

  const handleResetZoom = () => {
    setExtendedWidth(options?.width || 0);
    setIsChartZoomed(false);
  };

  const handleZoomIn = () => {
    if (!options || !options.width) return;
    if (extendedWidth + ZOOM_FACTOR >= 2 * options.width) return;
    setExtendedWidth((prev) => prev + ZOOM_FACTOR);
    if (!isChartZoomed) setIsChartZoomed(true);
  };

  const handleZoomOut = () => {
    if (!options || !options.width) return;
    const updatedWidth = Math.max(options?.width, extendedWidth - ZOOM_FACTOR);
    setExtendedWidth(updatedWidth);
    if (!isChartZoomed) setIsChartZoomed(true);
  };

  const drawZoomControls = () => {
    if (!options || !options.scrollablePanel) return;

    const svgContainer = document.getElementById('cw-gantt-chart-container');
    const existingControls = document.getElementById('zoom-controls-html');
    if (existingControls) existingControls.remove();

    if (!svgContainer) return;

    svgContainer.style.position = 'relative';

    const controlsDiv = document.createElement('div');
    controlsDiv.setAttribute('id', 'zoom-controls-html');
    controlsDiv.style.position = 'absolute';
    controlsDiv.style.right = '16px';
    controlsDiv.style.bottom = '16px';
    controlsDiv.style.display = 'flex';
    controlsDiv.style.flexDirection = 'column';
    controlsDiv.style.gap = '8px';
    controlsDiv.style.zIndex = '10';

    const resetDiv = document.createElement('div');
    resetDiv.style.background = '#0A343C';
    resetDiv.style.borderRadius = '4px';
    resetDiv.style.padding = '8px';
    resetDiv.style.display = 'flex';
    resetDiv.style.justifyContent = 'center';

    const zoomDiv = document.createElement('div');
    zoomDiv.style.background = '#0A343C';
    zoomDiv.style.borderRadius = '4px';
    zoomDiv.style.padding = '8px';
    zoomDiv.style.display = 'flex';
    zoomDiv.style.flexDirection = 'column';
    zoomDiv.style.gap = '8px';

    const createButton = (
      icon: string | { src: string },
      onClick: () => void
    ) => {
      const buttonSize = 24;
      const iconSize = 16;

      const button = document.createElement('button');
      button.style.width = `${buttonSize}px`;
      button.style.height = `${buttonSize}px`;
      button.style.border = 'none';
      button.style.borderRadius = '4px';
      button.style.display = 'flex';
      button.style.alignItems = 'center';
      button.style.justifyContent = 'center';
      button.style.cursor = 'pointer';
      button.style.background = 'transparent';
      button.onclick = onClick;

      const iconContainer = document.createElement('div');
      iconContainer.style.width = `${iconSize}px`;
      iconContainer.style.height = `${iconSize}px`;
      iconContainer.style.display = 'flex';
      iconContainer.style.alignItems = 'center';
      iconContainer.style.justifyContent = 'center';

      const iconSrc = typeof icon === 'string' ? icon : icon?.src;

      if (iconSrc?.trim().startsWith('<svg')) {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = iconSrc.trim();
        const svg = wrapper.querySelector('svg');
        if (svg) {
          svg.setAttribute('width', `${iconSize}`);
          svg.setAttribute('height', `${iconSize}`);
          svg.style.display = 'block';
          iconContainer.appendChild(svg);
        }
      } else {
        const img = document.createElement('img');
        img.src = iconSrc;
        img.width = iconSize;
        img.height = iconSize;
        img.style.display = 'block';
        iconContainer.appendChild(img);
      }

      button.appendChild(iconContainer);
      return button;
    };

    const resetButton = createButton(
      isChartZoomed ? fitToScreenEnabled : fitToScreenDisabled,
      handleResetZoom
    );
    const zoomInButton = createButton(zoomInSvg, handleZoomIn);
    const zoomOutButton = createButton(zoomOutSvg, handleZoomOut);

    resetDiv.appendChild(resetButton);
    zoomDiv.appendChild(zoomInButton);
    zoomDiv.appendChild(zoomOutButton);

    controlsDiv.appendChild(resetDiv);
    controlsDiv.appendChild(zoomDiv);
    svgContainer.appendChild(controlsDiv);
  };

  const drawHeaderColumn = () => {
    if (!options || !options.svg || !options.timelineSvg) return;

    options.timelineSvg?.select('.header-top-line')?.remove();
    options.fixedPanel?.select('.header-bottom-line')?.remove();

    options.timelineSvg
      ?.append('line')
      .attr('class', 'header-top-line')
      .style('stroke', '#FFFFFF')
      .attr('stroke-opacity', '0.12')
      .attr('x1', 0)
      .attr('y1', 50)
      .attr('x2', LEFT_PANEL_WIDTH + bufferSpaceWidth)
      .attr('y2', 50);

    options.fixedPanel
      ?.append('line')
      .attr('class', 'header-bottom-line')
      .style('stroke', '#FFFFFF')
      .attr('stroke-opacity', '0.12')
      .attr(
        'x1',
        getVisibleDataLength(formattedData) * 50 +
          ZOOM_CONTROL_AREA_HEIGHT +
          HEIGHT_OFFSET
      )
      .attr('y1', 50)
      .attr('x2', LEFT_PANEL_WIDTH + bufferSpaceWidth)
      .attr('y2', 50);

    d3.select('#expandcollapse').remove();

    const onClickEvent = (val: boolean) => {
      options.data.forEach((d) => {
        rowCollapsed[d.id] = val;
        setRowCollapsed({ ...rowCollapsed });
      });
      setIsCollapsed(val);
    };

    drawColExpandIcon(0, 41, options, onClickEvent);
  };

  const drawChevron = (
    x: number,
    y: number,
    options: ChartInternalOptions,
    rotate: boolean,
    length: number,
    onClick: (id: string) => void,
    d: DataModel
  ) => {
    const onChevRightClicked = () => {
      onClick(d.id);
    };
    if (rotate) {
      chevronRightIcon(
        x,
        y,
        options.svg,
        length,
        onChevRightClicked,
        '#8C9EA1'
      );
    } else {
      chevronDownIcon(x, y, options.svg, length, onChevRightClicked, '#8C9EA1');
    }
  };

  const drawColExpandIcon = (
    x: number,
    y: number,
    options: ChartInternalOptions,
    onClick: (state: boolean) => void
  ) => {
    if (!options || !options.timelineSvg) return;
    const onExpandClicked = () => {
      onClick(false); // set isCollapsed = false
      setRowCollapsed(() => {
        const collapsedState: Record<string, boolean> = {};
        const collapseAll = (node: DataModel) => {
          collapsedState[node.id] = true;
          if (node.children) node.children.forEach(collapseAll);
        };
        // First trace expanded 3 levels
        // All other traces collapsed
        formattedData.forEach((trace, idx) => {
          if (idx === 0) {
            expandNLevels(trace, collapsedState, 3);
          } else {
            collapseAll(trace);
          }
        });
        return collapsedState;
      });
    };

    const onCollapseClicked = () => {
      onClick(true);
      // Collapse everything except root trace nodes
      setRowCollapsed(() => {
        const collapsedState: Record<string, boolean> = {};
        const collapseAll = (node: DataModel) => {
          collapsedState[node.id] = true;
          if (node.children) node.children.forEach(collapseAll);
        };
        formattedData.forEach((trace) => {
           collapseAll(trace);
        });
        return collapsedState;
      });
    };

    if (!isColapsed) {
      options.timelineSvg
        .append('g')
        .attr('id', 'expandcollapse')
        .append('text')
        .attr('x', x)
        .attr('y', y)
        .attr('fill', '#34BCAF')
        .attr('font-size', '11px')
        .attr('cursor', 'pointer')
        .text('Collapse')
        .style('pointer-events', 'all')
        .on('click', onCollapseClicked);
    } else {
      options.timelineSvg
        .append('g')
        .attr('id', 'expandcollapse')
        .append('text')
        .attr('x', x)
        .attr('y', y)
        .attr('fill', '#34BCAF')
        .attr('font-size', '11px')
        .attr('cursor', 'pointer')
        .text('Expand')
        .style('pointer-events', 'all')
        .on('click', onExpandClicked);
    }
  };

  const drawAxis = () => {
    if (options === undefined || options.svg === undefined) return;
    const padding = Math.ceil(maxDuration * 0.1); // 10% padding

    const x = d3
      .scaleLinear()
      .domain([0, maxDuration + padding]) // Set the domain to the visible time range
      .range([LEFT_PANEL_WIDTH + bufferSpaceWidth, extendedWidth]); // top axis starts
    options.linearScale = x;

    // Pick one unit (ms/s/min/hr) for the whole axis and generate whole-number
    // Ticks are generated off the real (unpadded) maxDuration and clamped to it,
    // so the 10% padding never produces a tick past the actual end of the data
    // (which would otherwise land on top of, or beyond, the end-of-chart marker).
    const { divisor, unitLabel } = getDurationUnit(maxDuration);
    const unitMax = maxDuration / divisor;
    const unitScale = d3.scaleLinear().domain([0, unitMax]);
    const rawUnitTicks = unitScale.ticks().filter((t) => t <= unitMax);
    const wholeUnitTicks = Array.from(
      new Set(rawUnitTicks.map((t) => Math.round(t)))
    ).filter((t) => t <= unitMax);

    // On short ranges, d3's step can be a fraction (e.g. 0.2s), so
    // rounding every tick to a whole number collapses most of them into
    // duplicates and leaves a sparse axis. When that happens, fall
    // back to d3's raw values as-is (already clean steps like
    // 0.2/0.5/1/2/5, not floating-point noise) instead of losing density.
    const MIN_WHOLE_UNIT_TICKS = 5;
    const unitTicks =
      wholeUnitTicks.length >= MIN_WHOLE_UNIT_TICKS
        ? wholeUnitTicks
        : rawUnitTicks;
    const tickValues = unitTicks.map((t) => t * divisor);

    const xAxis = d3
      .axisTop(x)
      .tickValues(tickValues)
      .tickFormat((d) => {
        const value = Math.round((Number(d) / divisor) * 100) / 100;
        return `${value}${unitLabel}`;
      });

    options.timelineSvg
      ?.append('g')
      .attr('transform', 'translate(0,50)') // This controls the vertical position of the Axis
      .attr('class', 'axisLight')
      .call(xAxis)
      .selectAll('.tick text')
      .attr('dx', function (d) {
        return d === 0 ? '10' : '0'; // Move the 0ms label right
      });

    d3.selectAll('.domain').attr('opacity', '0.12');
    d3.selectAll('.tick line').attr('stroke', '0');

    const y = d3
      .scaleLinear()
      .domain([0, options.data.length + 1]) // This is what is written on the Axis: from 0 to 100
      .range([
        0,
        getVisibleDataLength(formattedData) * 50 +
          ZOOM_CONTROL_AREA_HEIGHT +
          HEIGHT_OFFSET,
      ]); // it sets the height of axis, 0 to count of expanded rows * their height + zoom area.

    // Draw the axis
    options.fixedPanel
      ?.append('g')
      .attr('transform', 'translate(0,0)') // This controls the vertical position of the Axis
      .attr('opacity', '0.12')
      .call(d3.axisLeft(y))
      .attr('class', 'axisLight');
  };
  const drawRowBackgrounds = (
    opts: ChartInternalOptions,
    d: DataModel,
    y: number,
    fill: string
  ) => {
    const onMouseOver = () => {
      const sb = document.getElementById(`chartBar-scrollable-${d.id}`);
      if (sb) d3.select(sb).attr('fill', hoverFill);
      const fb = document.getElementById(`chartBar-fixed-${d.id}`);
      if (fb) d3.select(fb).attr('fill', hoverFill);
      if (d.index === 1) {
        d3.select(`[id="first-data-bar"]`).attr('fill', hoverFill);
      }
    };
    const onMouseOut = () => {
      const fillColor = selectedSpanId === d.id ? hoverFill : normalFill;
      const sb = document.getElementById(`chartBar-scrollable-${d.id}`);
      if (sb) d3.select(sb).attr('fill', fillColor);
      const fb = document.getElementById(`chartBar-fixed-${d.id}`);
      if (fb) d3.select(fb).attr('fill', fillColor);
      if (d.index === 1) {
        d3.select(`[id="first-data-bar"]`).attr('fill', '#08242F');
      }
    };

    opts.scrollablePanel
      ?.append('rect')
      .attr('id', `chartBar-scrollable-${d.id}`)
      .attr('x', LEFT_PANEL_WIDTH + bufferSpaceWidth)
      .attr('y', y)
      .attr('width', extendedWidth)
      .attr('height', ROW_HEIGHT)
      .attr('fill', fill)
      .style('cursor', 'pointer')
      .on('mouseover', onMouseOver)
      .on('mouseout', onMouseOut);

    opts.fixedPanel
      ?.append('rect')
      .attr('id', `chartBar-fixed-${d.id}`)
      .attr('x', 0)
      .attr('y', y)
      .attr('width', LEFT_PANEL_WIDTH + bufferSpaceWidth)
      .attr('height', ROW_HEIGHT)
      .attr('fill', fill)
      .style('cursor', 'pointer')
      .on('mouseover', onMouseOver)
      .on('mouseout', onMouseOut);
  };

  const drawSeparatorLine = (
    opts: ChartInternalOptions,
    y: number,
    opacity: number = 0.12,
    visible: boolean = true
  ) => {
    const vis = visible ? 'visible' : 'hidden';
    opts.scrollablePanel
      ?.append('line')
      .attr('class', 'lineOverride')
      .style('stroke', '#FFFFFF')
      .attr('stroke-opacity', opacity)
      .attr('x1', LEFT_PANEL_WIDTH + bufferSpaceWidth)
      .attr('y1', y)
      .attr('x2', extendedWidth)
      .attr('y2', y)
      .style('visibility', vis);
    opts.fixedPanel
      ?.append('line')
      .attr('class', 'lineOverride')
      .style('stroke', '#FFFFFF')
      .attr('stroke-opacity', opacity)
      .attr('x1', 0)
      .attr('y1', y)
      .attr('x2', extendedWidth)
      .attr('y2', y)
      .style('visibility', vis);
  };

  const drawRow = (
    d: DataModel,
    options: ChartInternalOptions,
    barPositionMap: TBarPositionMap,
    depth: number = 0
  ) => {
    // Fact node
    if (d.spanType === 'fact') {
      visibleIndex = visibleIndex + 1;
      const factY = (visibleIndex - 1) * ROW_HEIGHT;


      drawRowBackgrounds(options, d, factY, normalFill);
      drawSeparatorLine(options, factY + ROW_HEIGHT);

      // Fact label
      options.scrollablePanel
        ?.append('text')
        .attr('class', 'normalText')
        .style('fill', '#C1C9CF')
        .text(d.label || 'Fact')
        .attr('x', LEFT_PANEL_WIDTH + bufferSpaceWidth + TEXT_IN_BARS_LEFT_OFFSET)
        .attr('y', factY + 32)
        .attr('font-size', 18)
        .attr('font-weight', '500')
        .style('pointer-events', 'none');

      if (loadingTraceId === d.id) {
        options.scrollablePanel
          ?.append('text')
          .attr('class', 'normalText')
          .style('fill', '#8C9EA1')
          .text('Loading...')
          .attr('x', LEFT_PANEL_WIDTH + bufferSpaceWidth + TEXT_IN_BARS_LEFT_OFFSET + (d.label?.length || 0) * 8 + 10)
          .attr('y', factY + 32)
          .attr('font-size', 10)
          .style('pointer-events', 'none');
      }

      // Index number
      const indexText = options.fixedPanel
        .append('text')
        .attr('class', 'normalText')
        .style('fill', '#8C9EA1')
        .text(`${d.index}`)
        .attr('x', 12)
        .attr('y', factY + 29)
        .attr('text-anchor', 'start')
        .attr('font-size', 12)
        .style('pointer-events', 'none');

      const indexWidth = indexText.node()?.getComputedTextLength() || 15;

barPositionMap[d.id] = {
  x: 42,
  y: factY + 25,
  width: 0,
  height: 0,
  startms: 0,
  curveType: 'Double-Beizer',
  leftOffsetForConnectingLine: (depth + 1) * 10,
  parentId: d.parentId,
  spanType: 'fact',
} as any;


      // Draw children and chevron
      if (d.children && d.children.length) {
        if (!rowCollapsed[d.id]) {
          d.children.forEach((c) => {
            // Manually link root traces to the fact node
            if (!c.parentId) c.parentId = d.id;
            drawRow(c, options, barPositionMap, depth + 1);
          });
        }
        drawChevron(12 + indexWidth + 3, factY + 20, options, rowCollapsed[d.id], 10, onChevronClick, d);
      }

      return;
    }
    const shoudlBeInvisible = isItemCollapsed(d);
    const startPositionY = 0;
    const barThickness = 10;
    visibleIndex = shoudlBeInvisible ? visibleIndex : visibleIndex + 1;

    // Rendering the bars
    const barStat: { [key: string]: number } = {
      topY: startPositionY + (visibleIndex - 1) * ROW_HEIGHT,
      x: getXPointOnChart(options, d.startms || 0),
      y: startPositionY + (visibleIndex - 1) * ROW_HEIGHT + 10,
      height: shoudlBeInvisible ? 0 : barThickness,
      width:
        getXPointOnChart(options, d.duration) - getXPointOnChart(options, 0),
    };

    const barPositionMapItem = {
      x: barStat.x,
      y: barStat.y,
      width: barStat.width,
      height: barStat.height,
      startms: d.startms || 0,
      curveType: 'Single-Beizer',
      leftOffsetForConnectingLine: (depth + 1) * 10, //10px per line, vertical segment.
      parentId: d.parentId,
      spanType: d.spanType,
    };
    barPositionMap[d.id] = barPositionMapItem as any;


    const isSelected = selectedSpanId === d.id;
    const defaultFill = isSelected ? hoverFill : normalFill;

    // Apply opacity based on filtered state
    const opacity = d.isFiltered ? 0.3 : 1;
    const cursor = onClick && !d.isFiltered ? 'pointer' : 'default';

    drawRowBackgrounds(options, d, barStat.topY, defaultFill);

    // represents the data bar in row.
    const width = Math.max(barStat.width, 5);
    const height = d.index === 1 ? barStat.height + 2 : barStat.height; //2px extra for first row to maintain a visible height and rounded shape.
    const r = 8;

    const pathData = `
  M${barStat.x},${barStat.y}
  H${barStat.x + width}
  V${barStat.y + height}
  Q${barStat.x + width},${barStat.y + height - r} ${barStat.x + width - r},${
    barStat.y + height - r
  }
  H${barStat.x + r}
  Q${barStat.x},${barStat.y + height - r} ${barStat.x},${barStat.y + height}
  Z
`;

    const bar =
      d.index === 1
        ? options.scrollablePanel
            ?.append('path')
            .attr('class', 'chartBar')
            .attr('d', pathData)
            .attr('fill', d.color)
            .style('opacity', opacity)
            .style('cursor', cursor)
        : options.scrollablePanel
            ?.append('rect')
            .attr('class', 'chartBar')
            .attr('x', barStat.x)
            .attr('y', barStat.y)
            .attr('width', width < 5 ? 5 : width)
            .attr('height', height)
            .attr('fill', d.color)
            .style('opacity', opacity)
            .style('cursor', cursor);

    drawSeparatorLine(options, barStat.topY + ROW_HEIGHT, 0.12, !shoudlBeInvisible);

    bar?.on('click', function () {
      if (onClick) {
        onClick(d.id);
        clearAllSelections();
      }
    });

    const baseLabel = d.displaySpanType;
    let labelText;
    // If entity count is 1 and name exists - show name, else show count
    let entityDisplay = '';

    if (d.entityCount) {
    if (d.entityCount === 1 && d.entity?.[0]?.name) {
    entityDisplay = d.entity[0].name;
     } else {
    entityDisplay = `${d.entityCount} ${messages?.GanttChart?.trace_span_entities}`;
     }
   }

    const effectiveLabel = baseLabel || d.label || 'Span';

    const formattedDur = formatDuration(d.duration);

    if (effectiveLabel?.startsWith('workflow')) {
      labelText = `${effectiveLabel} | ${formattedDur}${d.monocleLang ? ` | ${d.monocleLang}` : ''}${d.monocleVer ? ` | ${d.monocleVer}` : ''}`;
    } else if (d.spanType === 'http.process' && d.statusCode !== 'error') {
      labelText = `${effectiveLabel} | ${d.method} ${d.route} | ${formattedDur}`;
    } else if (d.spanType === 'http.process' && d.statusCode === 'error') {
      labelText = `${effectiveLabel} | ${d.method} ${d.route} ${
        d.exceptionType ? '|' + d.exceptionType : ''
      } | ${formattedDur}`;
    } else
      labelText = `${effectiveLabel} | ${formattedDur}${entityDisplay ? ` | ${entityDisplay}` : ''}`;

    const renderedText = options.scrollablePanel
      ?.append('text')
      .attr('class', 'normalText')
      .style('fill', '#8C9EA1')
      .text(labelText)
      .attr(
        'x',
        barStat.x +
          TEXT_IN_BARS_LEFT_OFFSET +
          (d.index > 1 && d.children && d.children?.length > 0
            ? CONNECTING_LINE_LEFT_OFFSET
            : 0) //add the lines margin for 1) only child nodes 2) only ones who has child.
      )
      .attr('y', barStat.y + (d.index === 1 ? 15 : 25)) //15 for the first row to overlap with the bar
      .style('max-width', 100)
      .style('break-word', 'break-all')
      .attr('text-anchor', 'right')
      .attr('font-size', 12)
      .style('visibility', shoudlBeInvisible ? 'hidden' : 'visible')
      .style('opacity', opacity)
      .style('pointer-events', 'none');

    // Add Loader
    if (loadingTraceId === d.id || localLoadingTraceId === d.id) {
      renderedText
        .append('tspan')
        .attr('class', 'loading-text')
        .style('fill', '#8C9EA1')
        .style('font-size', '11px')
        .text(' | Loading...');
    }
    // Add error indicator
    if (errorTraceId === d.id && loadingTraceId !== d.id && localLoadingTraceId !== d.id) {
      // measure text to place error icon after the name
      const textWidth = renderedText?.node()?.getComputedTextLength() || 0;
      const baseIconX = barStat.x + TEXT_IN_BARS_LEFT_OFFSET + (d.index > 1 && d.children && d.children.length > 0 ? CONNECTING_LINE_LEFT_OFFSET : 0);
      const iconX = baseIconX + textWidth + 8;
      const iconY = barStat.y + (d.index === 1 ? 15 : 25) - 10;

      errorIcon(
        iconX,
        iconY,
        options.scrollablePanel,
        12,
        errorTraceMessage || 'Error loading traces'
      );
    }

    // Handle text overflow
    const bbox = renderedText?.node()?.getBBox();
    const currentDomain = options.linearScale?.domain();
    const currentRange = options.linearScale?.range();
    const textEndLocation =
      (bbox?.x || 0) + getXPointOnChart(options, bbox?.width || 0);
    if (textEndLocation > (currentRange ? currentRange[1] : 800)) {
      renderedText?.attr(
        'x',
        getXPointOnChart(options, currentDomain ? currentDomain[1] : 800) -
          (bbox?.width || 0) -
          10
      );
    }

    let indexWidth = 15;
    if (!shoudlBeInvisible) {
      const indexText = options.fixedPanel
        .append('text')
        .attr('class', 'normalText')
        .style('fill', '#8C9EA1')
        .text(`${d.index}`)
        .attr('x', 12)
        .attr('y', barStat.y + 19)
        .attr('text-anchor', 'start')
        .attr('font-size', 12)
        .style('transform', 'rotate(90)')
        .style('pointer-events', 'none');

      indexWidth = indexText.node()?.getComputedTextLength() || 15;
    }

    if (d.children && d.children.length) {
      if (!rowCollapsed[d.id]) {
        d.children.forEach((c) => {
          drawRow(c, options, barPositionMap, depth + 1);
        });
      }
    }

    const isPlaceholder =
      d.spanType === 'workflow' &&
      onTraceExpand &&
      (!d.children || d.children.length === 0) &&
      d.entityCount === undefined;

    if (!shoudlBeInvisible && (d.children?.length || isPlaceholder)) {
        drawChevron(
        12 + indexWidth + 3,
          barStat.y + 10,
          options,
          rowCollapsed[d.id] ?? true,
          10,
          onChevronClick,
          d
        );
    }
  };

  const drawEmptyRows = (options: ChartInternalOptions, count: number) => {
    if (!options || count <= 0) return;

    const startPositionY = 0;
    const baseIndex = visibleIndex; // Start after the last data row

    for (let i = 0; i < count; i++) {
      const rowY = startPositionY + (baseIndex + i) * ROW_HEIGHT;

      options.scrollablePanel
        ?.append('rect')
        .attr('id', `emptyRow-scrollable-${i}`)
        .attr('x', LEFT_PANEL_WIDTH + bufferSpaceWidth)
        .attr('y', rowY)
        .attr('width', extendedWidth)
        .attr('height', ROW_HEIGHT)
        .attr('fill', 'rgba(255,255,255,0)')
        .on('mouseover', function () {
          d3.select(`#emptyRow-fixed-${i}`).attr(
            'fill',
            'rgba(255,255,255,0.12)'
          );
          d3.select(this).attr('fill', 'rgba(255,255,255,0.12)');
        })
        .on('mouseout', function () {
          d3.select(this).attr('fill', 'rgba(255,255,255,0)');
          d3.select(`#emptyRow-fixed-${i}`).attr('fill', 'rgba(255,255,255,0)');
        });

      options.fixedPanel
        ?.append('rect')
        .attr('id', `emptyRow-fixed-${i}`)
        .attr('x', 0)
        .attr('y', rowY)
        .attr('width', LEFT_PANEL_WIDTH + bufferSpaceWidth)
        .attr('fill', 'rgba(255,255,255,0)')
        .attr('height', ROW_HEIGHT)
        .on('mouseover', function () {
          d3.select(this).attr('fill', 'rgba(255,255,255,0.12)');
          d3.select(`#emptyRow-scrollable-${i}`).attr(
            'fill',
            'rgba(255,255,255,0.12)'
          );
        })
        .on('mouseout', function () {
          d3.select(this).attr('fill', 'rgba(255,255,255,0)');
          d3.select(`#emptyRow-scrollable-${i}`).attr(
            'fill',
            'rgba(255,255,255,0)'
          );
        });

      drawSeparatorLine(options, rowY + ROW_HEIGHT, i === count - 1 ? 0.12 : 0.03);
    }
  };

  const drawConnectingLines = (
    d: DataModel,
    barPositionMap: TBarPositionMap
  ) => {
    if (
      d.parentId &&
      barPositionMap[d.parentId] &&
      barPositionMap[d.id] &&
      options
    ) {
      const parent = barPositionMap[d.parentId];
      const child = barPositionMap[d.id];

      const curveType = parent.curveType;
      if (curveType === 'Double-Beizer') {
        const fixedPanelWidth = LEFT_PANEL_WIDTH + bufferSpaceWidth;
        const parentX = parent.x;
        const parentY = parent.y + (parent.height * 2) / 3;
        const childX = child.x;
        const childY = child.y + child.height / 3;

        const radius = 8;
      let cornerX =
        parentX >= fixedPanelWidth + 75
          ? parentX - 50
          : parent.leftOffsetForConnectingLine + LEFT_PANEL_WIDTH;

      if (parent.spanType === 'fact') {
        // Anchor to exactly 10px outside the root trace's own child-curve spine
        cornerX = child.leftOffsetForConnectingLine + LEFT_PANEL_WIDTH - 10;
      }

        let fixedPanelPath = ``,
          scrollablePanelPath = ``;

        //for the starting horizontal line
        if (parentX >= fixedPanelWidth) {
          scrollablePanelPath = `
          M${parentX},${parentY}
          L${cornerX + radius},${parentY}
          `;
        } else {
          //added dummy M to get rid of warnings: missing a turning point M.
          scrollablePanelPath = `
          M0,0
          `;
        }
        fixedPanelPath = `
          M${fixedPanelWidth},${parentY}
          L${cornerX + radius},${parentY}
          `;

        //define the vertical segment along with rounded corners
        if (cornerX + radius > fixedPanelWidth) {
          //vertical segment is in scrollable panel
          scrollablePanelPath += `
          Q${cornerX},${parentY} ${cornerX},${parentY + radius}
          L${cornerX},${childY - radius}
          Q${cornerX},${childY} ${cornerX + radius},${childY}
          `;
        } else {
          //vertical segment is in fixed panel
          fixedPanelPath += `
          Q${cornerX},${parentY} ${cornerX},${parentY + radius}
          L${cornerX},${childY - radius}
          Q${cornerX},${childY} ${cornerX + radius},${childY}
          L${fixedPanelWidth}, ${childY}
          `;
        }

        // last horizontal segment.
        if (childX > fixedPanelWidth) {
          //line goes to scrollable panel as well
          scrollablePanelPath += `
          M${cornerX + radius}, ${childY}
          L${childX}, ${childY}`;
        }

        options.fixedPanel
          ?.append('path')
          .attr('class', 'connectionLine')
          .attr('d', fixedPanelPath)
          .attr('stroke', '#40565A')
          .attr('fill', 'none')
          .attr('stroke-width', 1);
        options.scrollablePanel
          ?.append('path')
          .attr('class', 'connectionLine')
          .attr('d', scrollablePanelPath)
          .attr('stroke', '#40565A')
          .attr('fill', 'none')
          .attr('stroke-width', 1);
      } else {
        const parentX =
          d.parentId === options.data[0].id
            ? parent.x
            : parent.x + CONNECTING_LINE_LEFT_OFFSET;
        const parentY = parent.y + parent.height; // bottom of parent bar (bar height = 10)
        const childX = child.x;
        const childY = child.y + child.height / 3;
      const fixedPanelWidth = LEFT_PANEL_WIDTH + bufferSpaceWidth;
      const turnY = childY;
      const radius = 8;

      if (parent.spanType === 'fact') {
        const factX = fixedPanelWidth + TEXT_IN_BARS_LEFT_OFFSET;
        const radius = 8; 
        const spineX = child.leftOffsetForConnectingLine + LEFT_PANEL_WIDTH - 2;

        const scrollableStartPath = `
          M${factX},${parentY}
          L${fixedPanelWidth},${parentY}
        `;

        const fixedPath = `
          M${fixedPanelWidth},${parentY}
          L${spineX + radius},${parentY}
          Q${spineX},${parentY} ${spineX},${parentY + radius}
          L${spineX},${turnY - radius}
          Q${spineX},${turnY} ${spineX + radius},${turnY}
          L${Math.min(fixedPanelWidth, childX)},${turnY}
        `;

        options.scrollablePanel
          ?.append('path')
          .attr('class', 'connectionLine')
          .attr('d', scrollableStartPath)
          .attr('stroke', '#40565A')
          .attr('fill', 'none')
          .attr('stroke-width', 1);

        options.fixedPanel
          ?.append('path')
          .attr('class', 'connectionLine')
          .attr('d', fixedPath)
          .attr('stroke', '#40565A')
          .attr('fill', 'none')
          .attr('stroke-width', 1);

        if (childX > fixedPanelWidth) {
          const scrollableEndPath = `
            M${fixedPanelWidth},${turnY}
            L${childX},${turnY}
          `;
          options.scrollablePanel
            ?.append('path')
            .attr('class', 'connectionLine')
            .attr('d', scrollableEndPath)
            .attr('stroke', '#40565A')
            .attr('fill', 'none')
            .attr('stroke-width', 1);
        }
      } else {
        const maxRadius = Math.min(radius, Math.max(0, childX - parentX));
        
        if (parentX < fixedPanelWidth) {
          const fixedEndX = Math.min(fixedPanelWidth, childX);
          const fixedPath = `
            M${parentX},${parentY}
            L${parentX},${turnY - maxRadius}
            Q${parentX},${turnY} ${parentX + maxRadius},${turnY}
            L${fixedEndX},${turnY}
          `;
          options.fixedPanel
            ?.append('path')
            .attr('class', 'connectionLine')
            .attr('d', fixedPath)
            .attr('stroke', '#40565A')
            .attr('fill', 'none')
            .attr('stroke-width', 1);
          
          // If it continues into scrollable panel
          if (childX > fixedPanelWidth) {
             const scrollablePath = `
               M${fixedPanelWidth},${turnY}
               L${childX},${turnY}
             `;
             options.scrollablePanel
              ?.append('path')
              .attr('class', 'connectionLine')
              .attr('d', scrollablePath)
              .attr('stroke', '#40565A')
              .attr('fill', 'none')
              .attr('stroke-width', 1);
          }
        } else {
        const path = `
  M${parentX},${parentY}
  L${parentX},${turnY - maxRadius}
  Q${parentX},${turnY} ${parentX + maxRadius},${turnY}
  L${childX},${turnY}
`;

        options.scrollablePanel
          ?.append('path')
          .attr('class', 'connectionLine')
          .attr('d', path)
          .attr('stroke', '#40565A')
          .attr('fill', 'none')
          .attr('stroke-width', 1);
      }
    }
    }
  } 
  
    if (d.children && d.children.length) {
      d.children.forEach((d) => drawConnectingLines(d, barPositionMap)); // Recurse
    }
  };
  // by default made all single-beizer, now we update them, if any parent has its child too close, we create double beizer for them and hence for all children.
  function updateCurveTypes(d: DataModel, barPositionMap: TBarPositionMap) {
    const parentBarStat = barPositionMap[d.id];

    if (d.children && d.children.length) {
      for (const child of d.children) {
        const childBarStat = barPositionMap[child.id];

        if (childBarStat && parentBarStat) {
          if (Math.abs(parentBarStat.x - childBarStat.x) < 75) {
            //if less space, then double beizer
            parentBarStat.curveType = 'Double-Beizer';
          }
        }
        updateCurveTypes(child, barPositionMap);
      }
    }
  }

  const drawRects = () => {
    if (!options) return;

    // Clear old elements
    d3.selectAll('.chartBar').remove();
    options.scrollablePanel?.selectAll('.lineOverride').remove();
    options.fixedPanel
      ?.selectAll('.lineOverride:not(.divider-line):not(.header-bottom-line)')
      .remove();
    options.scrollablePanel?.selectAll('.normalText').remove();
    options.fixedPanel?.selectAll('.normalText').remove();
    options.scrollablePanel?.selectAll('.connectionLine').remove();
    options.scrollablePanel?.selectAll("[id^='chartBar-scrollable-']").remove();
    options.fixedPanel?.selectAll("[id^='chartBar-fixed-']").remove();
    options.scrollablePanel?.selectAll("[id^='emptyRow-scrollable-']").remove();
    options.fixedPanel?.selectAll("[id^='emptyRow-fixed-']").remove();
    options.scrollablePanel?.selectAll('.error-icon').remove();

    visibleIndex = 0;
    const barPositionMap: TBarPositionMap = {};

    options.data.forEach((d) => {
      drawRow(d, options, barPositionMap);
    });

    drawEmptyRows(options, EMPTY_ROWS_COUNT);

    for (let i = 0; i < options.data.length; i++) {
      updateCurveTypes(options.data[i], barPositionMap);
    }

    // find the number of double-beizer curves to decide how many lines we'll need to create.
    // const doubleBeizerCount = Object.values(barPositionMap).filter(
    //   (cur) => cur.curveType === 'Double-Beizer'
    // ).length;
  //  const newBufferSpaceWidth = Math.max(50, (doubleBeizerCount + 1) * 10);
    // if (newBufferSpaceWidth !== bufferSpaceWidth) {
    //   setBufferSpaceWidth(newBufferSpaceWidth);
    // }
    // we can draw 9 double-beizer curve lines in 100px buffer space, update it as per the count.
    options.data.forEach((d) => {
      drawConnectingLines(d, barPositionMap);
    });
  };

  const drawVerticalLines = () => {
    if (!options || !options.scrollablePanel) return;

    options.scrollablePanel.select('.vertical-lines-group')?.remove();
    options.fixedPanel?.select('.divider-line')?.remove();

    const group = options.scrollablePanel
      .append('g')
      .attr('class', 'vertical-lines-group');
    const totalHeight =
      getVisibleDataLength(formattedData) * 50 +
      ZOOM_CONTROL_AREA_HEIGHT +
      HEIGHT_OFFSET;

    options.fixedPanel
      ?.append('line')
      .attr('class', 'divider-line')
      .style('stroke', '#FFFFFF')
      .attr('stroke-opacity', 0.08)
      .attr('x1', LEFT_PANEL_WIDTH + bufferSpaceWidth)
      .attr('y1', 0)
      .attr('x2', LEFT_PANEL_WIDTH + bufferSpaceWidth)
      .attr('y2', totalHeight);

    options.linearScale?.ticks().forEach((tick) => {
      group
        ?.append('line')
        .attr('class', 'lineOverride')
        .style('stroke', '#FFFFFF')
        .attr('stroke-opacity', 0.12)
        .attr('x1', getXPointOnChart(options, tick.valueOf()))
        .attr('y1', 0)
        .attr('x2', getXPointOnChart(options, tick.valueOf()))
        .attr('y2', totalHeight);
    });

    group
      ?.append('line')
      .attr('class', 'lineOverride')
      .style('stroke', '#FFFFFF')
      .attr('stroke-opacity', 0.12)
      .attr(
        'x1',
        getXPointOnChart(
          options,
          options.linearScale ? options.linearScale?.domain()[1] : 0
        )
      )
      .attr('y1', 0)
      .attr(
        'x2',
        getXPointOnChart(
          options,
          options.linearScale ? options.linearScale?.domain()[1] : 0
        )
      )
      .attr('y2', totalHeight);
  };

  // used to draw the end line at max duration of the trace
  const drawEndLine = () => {
    if (!options || !options.timelineSvg || !options.scrollablePanel) return;
    const maxDurationPos = getXPointOnChart(options, maxDuration);
    const maxDurationLabel = formatDuration(maxDuration);

    // Skip drawing if max duration is outside the visible range
    if (maxDurationPos < 0 || maxDurationPos > extendedWidth) return;

    options.timelineSvg.select('.end-line-group')?.remove();
    options.scrollablePanel.select('.end-line-group')?.remove();
    const group = options.timelineSvg
      .append('g')
      .attr('class', 'end-line-group');
    const scrollableSegmentGroup = options.scrollablePanel
      .append('g')
      .attr('class', 'end-line-group');

    scrollableSegmentGroup
      ?.append('line')
      .attr('class', 'lineHighlight')
      .style('stroke', '#8C9EA1')
      .attr('x1', maxDurationPos)
      .attr('y1', 0)
      .attr('x2', maxDurationPos)
      .attr(
        'y2',
        getVisibleDataLength(formattedData) * 50 +
          ZOOM_CONTROL_AREA_HEIGHT +
          HEIGHT_OFFSET
      );

    const textElement = group
      ?.append('text')
      .attr('class', 'cwgcTextDark')
      .style('fill', 'black')
      .attr('x', maxDurationPos)
      .attr('y', 50)
      .style('text-anchor', 'middle')
      .style('font-size', '11px')
      .style('font-weight', '500')
      .style('stroke', 'back')
      .text(maxDurationLabel);

    const width = textElement?.node()?.getComputedTextLength();
    textElement?.remove();

    group
      ?.append('rect')
      .attr('x', maxDurationPos - (width || 0) / 2 - 8)
      .attr('y', 25)
      .attr('width', (width || 0) + 16)
      .attr('height', 21)
      .attr('rx', 3)
      .attr('stroke', 'none')
      .style('fill', '#C1C9CF');

    group
      ?.append('text')
      .attr('class', 'cwgcTextDark')
      .style('fill', 'black')
      .attr('x', maxDurationPos)
      .attr('y', 40)
      .style('text-anchor', 'middle')
      .style('font-size', '11px')
      .style('font-weight', '500')
      .style('stroke', 'back')
      .text(maxDurationLabel);

    const trianglePointer = d3.symbol().type(d3.symbolTriangle).size(25);
    group
      ?.append('path')
      .attr('d', trianglePointer)
      .attr('fill', '#C1C9CF')
      .attr('stroke', 'none')
      .attr(
        'transform',
        `translate(${getXPointOnChart(options, maxDuration)}, 47) rotate(180)`
      );
  };

  const getXPointOnChart = (options: ChartInternalOptions, point: number) => {
    return options.linearScale ? options.linearScale(point) : 100;
  };

  const getVisibleDataLength = (data: DataModel[]): number => {
    let count = 0;

    const checkItem = (item: DataModel) => {
      if (!isItemCollapsed(item)) {
        count++;
        if (
          item.children &&
          item.children.length > 0 &&
          !rowCollapsed[item.id]
        ) {
          item.children.forEach(checkItem);
        }
      }
    };

    data.forEach(checkItem);
    return count;
  };

  const isItemCollapsed = (item: DataModel): boolean => {
    if (!item.parentId) return false;
    if (rowCollapsed[item.parentId]) return true;
    const parent = findNodeById(data, item.parentId);
    return parent ? isItemCollapsed(parent) : false;
  };

  const findNodeById = (items: DataModel[], id: string): DataModel | null => {
    for (const item of items) {
      if (item.id === id) return item;
      if (item.children) {
        const found = findNodeById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  // Expand N levels of a subtree, collapse the rest
  const expandNLevels = (
    node: DataModel,
    state: Record<string, boolean>,
    levels: number
  ) => {
    let count = 0; // Track nodes expanded
    const traverse = (n: DataModel) => {
      const isPlaceholder = n.spanType === 'workflow' && (!n.children || n.children.length === 0);
      // Expand first N non-placeholder nodes
      if (count < levels && !isPlaceholder) {
        state[n.id] = false;
        count++;
      } else {
        state[n.id] = true;
        if (isPlaceholder && count < levels) count++;
      }
       // Recurse through children
      if (n.children) n.children.forEach(traverse);
    };
    traverse(node);
  };

  // Function to expand all parents of a given span ID
  const expandParentsOfSpan = (
    spanId: string,
    items: DataModel[],
    collapsedState: Record<string, boolean>
  ) => {
    const target = findNodeById(items, spanId);
    if (!target?.parentId) return;

    let parentId: string | undefined = target.parentId;
    while (parentId) {
      collapsedState[parentId] = false;
      const parent = findNodeById(items, parentId);
      parentId = parent?.parentId;
    }
  };

  return (
    <div id="cw-gantt-chart-container">
      <div id="timeline-axis-container"></div>
      <div id="gantt-chart-container"></div>
      <div id="tag"></div>
    </div>
  );
};
